import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type RadioButtonStateStyleConstantType,
    type RadioButtonMeasuresConstantType,
    type RadioButtonColorsConstantType,
    type RadioButtonTypeConstantType,
    type RadioButtonDynamicStyleType,
    type RadioButtonStateStyles,
    type RadioButtonMeasures,
    type RadioButtonColors,
    type RadioButtonTypes
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    SELECTION_CONTROL_ELEVATION_LEVEL
} from "../selectionControl/stylesheet";
import {
    getFocusRingStyle,
    getStateColorKey
} from "../../helpers/theme/state";
import {
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    getMotionTransition
} from "../../helpers/motion";
import {
    webStyle
} from "../../utils";

export const getRadioButtonMeasures = ({
    iconSizes,
    radiuses,
    borders
}: RadioButtonMeasuresConstantType): RadioButtonMeasures => {
    return {
        borderWidth: borders.selection,
        dotSize: iconSizes.small / 2,
        borderRadius: radiuses.full,
        ringSize: iconSizes.small
    };
};

export const getRadioButtonType = ({
    colors,
    type
}: RadioButtonTypeConstantType): RadioButtonTypes => {
    const filledColors = getTypeColors({
        variant: "filled",
        colors,
        type
    });

    return {
        selectedColor: filledColors.containerColor,
        overlayColor: getStateColorKey({
            type
        })
    };
};

export const getRadioButtonColors = ({
    currentType,
    isDisabled,
    isChecked,
    isError,
    colors
}: RadioButtonColorsConstantType): RadioButtonColors => {
    if(isDisabled) {
        const disabledFilledColors = getTypeColors({
            variant: "filled",
            type: "neutral",
            isDisabled,
            colors
        });

        const disabledOutlineColors = getTypeColors({
            variant: "outline",
            type: "neutral",
            isDisabled,
            colors
        });

        return {
            hoverBorderColor: disabledOutlineColors.borderColor,
            containerColor: disabledFilledColors.containerColor,
            overlayColor: disabledFilledColors.overlayColor,
            borderColor: disabledOutlineColors.borderColor,
            spinnerColor: disabledFilledColors.iconColor,
            dotColor: disabledFilledColors.iconColor
        };
    }

    const restColors = getTypeColors({
        variant: "outline",
        type: "neutral",
        colors
    });

    if(isChecked) {
        return {
            hoverBorderColor: currentType.selectedColor,
            containerColor: restColors.containerColor,
            spinnerColor: currentType.selectedColor,
            borderColor: currentType.selectedColor,
            overlayColor: currentType.overlayColor,
            dotColor: currentType.selectedColor
        };
    }

    const borderColor = isError ? colors.content.border.danger : colors.content.icon.low;

    return {
        hoverBorderColor: isError ? borderColor : colors.content.icon.mid,
        containerColor: restColors.containerColor,
        spinnerColor: currentType.selectedColor,
        overlayColor: currentType.overlayColor,
        dotColor: currentType.selectedColor,
        borderColor
    };
};

export const getRadioButtonStateStyle = ({
    isReduceMotionEnabled,
    currentMeasures,
    isFocusVisible,
    currentColors,
    durations,
    isHovered,
    isPressed,
    borders,
    colors,
    type
}: RadioButtonStateStyleConstantType): RadioButtonStateStyles => {
    const focusRingStyle = getFocusRingStyle({
        colorKey: getStateColorKey({
            type
        }),
        isFocusVisible,
        borders,
        colors
    });

    const styles = {
        loading: {
            ...focusRingStyle
        } as Mutable<ViewStyle>,
        ring: {
            ...focusRingStyle
        } as Mutable<ViewStyle>
    };

    if(isFocusVisible) {
        styles.loading.borderRadius = currentMeasures.borderRadius;
    }

    if(isHovered || isPressed) {
        styles.ring.borderColor = currentColors.hoverBorderColor;
    }

    Object.assign(styles.ring, webStyle(getMotionTransition({
        properties: [
            "background-color",
            "border-color"
        ],
        isReduceMotionEnabled,
        intent: "hover",
        durations
    })));

    return styles;
};

const stylesheet = StyleSheet.create({
    ring: {
        justifyContent: "center",
        borderStyle: "solid",
        alignItems: "center"
    }
});

export const useStyles = ({
    currentMeasures,
    currentColors,
    shadowAlpha,
    elevations,
    isDisabled,
    colors
}: RadioButtonDynamicStyleType) => {
    const styles = {
        ring: {
            backgroundColor: currentColors.containerColor,
            borderRadius: currentMeasures.borderRadius,
            borderWidth: currentMeasures.borderWidth,
            borderColor: currentColors.borderColor,
            height: currentMeasures.ringSize,
            width: currentMeasures.ringSize
        } as Mutable<ViewStyle>,
        dot: {
            borderRadius: currentMeasures.borderRadius,
            backgroundColor: currentColors.dotColor,
            height: currentMeasures.dotSize,
            width: currentMeasures.dotSize
        } as Mutable<ViewStyle>
    };

    if(!isDisabled) {
        Object.assign(styles.ring, getElevationStyle({
            level: SELECTION_CONTROL_ELEVATION_LEVEL,
            shadowColor: colors.system.shadow,
            shadowAlpha,
            elevations
        }));
    }

    return styles;
};
export default stylesheet;
